import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Chip } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import WayfinderMainCategoriesTemplate from "./WayfinderMainCategoriesTemplate";
import UseToast from "../UseToast";
import useS3Upload from "../../hooks/useS3Upload";
import { usePxToVw } from "../../hooks/usePxToVw";

const WayfinderMainSubCategory = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const { userId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();
  const navigate = useNavigate();
  const { pxToVw } = usePxToVw();
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [loadingText, setLoadingText] = useState(false);

  const { uploadFile, uploading } = useS3Upload({
    onSuccess: (fileUrl) => {

    },
    onError: (error) => {
      showErrorToast("Failed to upload logo");
      console.error("Upload error:", error);
    },
  });

  const [uploadedUrls, setUploadedUrls] = useState({
    upperLeft: {
      fileName: "",
      fileUrl: "",
      enabled: false,
      width: 100,
      height: 100,
    },
    upperRight: {
      fileName: "",
      fileUrl: "",
      enabled: false,
      width: 100,
      height: 100,
    },
    bottomLeft: {
      fileName: "",
      fileUrl: "",
      enabled: false,
      width: 100,
      height: 100,
    },
    bottomRight: {
      fileName: "",
      fileUrl: "",
      enabled: false,
      width: 100,
      height: 100,
    },
  });

  // Initial state with 4 corner logos
  const [subCategoryData, setSubCategoryData] = useState({
    userId: "",
    uploadedUrls: uploadedUrls,
    status: true,
    header: {
      enabled: true,
      text: "",
      color: "#000000",
      fontSize: 24,
      fontWeight: 600,
      textAlign: "center",
      opacity: 1,
      textShadowH: "2",
      textShadowV: "2",
      textShadowBlur: "4",
      textShadowColor: "#000000",
      fontFamily: "Arial",
      top: 10,
      left: 10,
    },
    description: {
      enabled: true,
      text: "",
      color: "#000000",
      fontSize: 16,
      fontWeight: 400,
      textAlign: "left",
      opacity: 1,
      textShadowH: "0",
      textShadowV: "0",
      textShadowBlur: "0",
      textShadowColor: "#000000",
      top: 60,
      left: 10,
      fontFamily: "Arial",
    },
    footer: {
      enabled: true,
      text: "",
      color: "#000000",
      fontSize: 14,
      fontWeight: 400,
      textAlign: "center",
      opacity: 0.8,
      textShadowH: "0",
      textShadowV: "0",
      textShadowBlur: "0",
      textShadowColor: "#000000",
      top: 250,
      left: 10,
      fontFamily: "Arial",
    },
    searchConfig: {
      enabled: true,
      placeholder: "Search...",
      backgroundColor: "#ffffff",
      textColor: "#000000",
      borderColor: "#cccccc",
      borderRadius: "4px",
      borderWidth: "1px",
      boxShadow: "",
      height: "40px",
      padding: "0 10px",
    },
  });

  useEffect(() => {
    fetchSubCategoryData();
  }, [userId]);



  const [uploadingStates, setUploadingStates] = useState({
    upperLeft: false,
    upperRight: false,
    bottomLeft: false,
    bottomRight: false,
  });

  const fetchSubCategoryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/wayfinder/main-subcategory/${userId}`
      );
      if (response.data.success) {
        const data = response.data.data;
        setSubCategoryData(data);

        const updatedUrls = { ...uploadedUrls };
        Object.keys(data.uploadedUrls || {}).forEach(position => {
          if (data.uploadedUrls[position]) {
            updatedUrls[position] = {
              fileName: data.uploadedUrls[position].fileName || "",
              fileUrl: data.uploadedUrls[position].fileUrl || "",
              enabled: data.uploadedUrls[position].enabled || false,
              width: data.uploadedUrls[position].width || 100,
              height: data.uploadedUrls[position].height || 100,
            };
          }
        });
        setUploadedUrls(updatedUrls);
      }
    } catch (error) {
      console.error("Error fetching subcategory data:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to load subcategory data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setSubCategoryData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTextStylingChange = (section, data) => {
    setSubCategoryData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Handle logo property changes
  const handleLogoChange = (position, field, value) => {
    setSubCategoryData((prev) => ({
      ...prev,
      logos: {
        ...prev.logos,
        [position]: {
          ...prev.logos[position],
          [field]: value,
        },
      },
    }));
  };

  const handleUploadLogo = async (file, position) => {
    if (!file) {
      showErrorToast(`Please select a file for ${position} first!`);
      return;
    }

    setUploadingStates((prev) => ({ ...prev, [position]: true }));

    try {

      const fileUrl = await uploadFile(file);


      setUploadedUrls((prev) => ({
        ...prev,
        [position]: {
          fileName: file.name,
          fileUrl,
          enabled: true,
          width: prev[position]?.width || 100,
          height: prev[position]?.height || 100,
        },
      }));

      showSuccessToast(`${position} uploaded successfully!`);
    } catch (error) {
      console.error("Upload failed:", error);
      showErrorToast(`Failed to upload ${position}`);
    } finally {
      setUploadingStates((prev) => ({ ...prev, [position]: false }));
    }
  };

  const handleDeleteLogo = async (position) => {
    try {
      setUploadedUrls((prev) => ({
        ...prev,
        [position]: {
          ...prev[position],
          fileName: "",
          fileUrl: "",
          enabled: false,
        },
      }));
      showSuccessToast("Logo removed successfully");
    } catch (error) {
      showErrorToast("Failed to remove logo");
    }
  };

  console.log('uploaded urls outside', uploadedUrls);


  const handleSave = async () => {
    try {
      setLoading(true);

      const payload = {
        ...subCategoryData,
        uploadedUrls: uploadedUrls,
      }

      const response = await axios.post(
        `/api/wayfinder/save-main-subcategory/${userId}`,
        payload
      );

      console.log('uploaded urls', uploadedUrls);


      console.log('subCategoryData urls', payload);
      if (response.data.success) {
        showSuccessToast("Subcategory settings saved successfully!");
      } else {
        showErrorToast(response.data.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error saving subcategory data:", error);
      showErrorToast("Failed to save subcategory settings");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/wayfinder/reset-main-subcategory/${userId}`
      );
      if (response.data.success) {
        setSubCategoryData(response.data.data);
        showSuccessToast("Settings reset successfully");
      }
    } catch (error) {
      showErrorToast("Failed to reset settings");
    } finally {
      setLoading(false);
    }
  };

  const getPositionName = (position) => {
    const names = {
      upperLeft: "Upper Left",
      upperRight: "Upper Right",
      bottomLeft: "Bottom Left",
      bottomRight: "Bottom Right",
    };
    return names[position] || position;
  };


  const renderLivePreview = () => {
    const textShadowStyle = (data) =>
      `${data.textShadowH}px ${data.textShadowV}px ${data.textShadowBlur}px ${data.textShadowColor}`;

    return (
      <Box
        sx={{
          position: "relative",
          height: '1920px',
          width: '70vw',
          display: "flex",
          alignSelf: "center",
          backgroundColor: "#f5f7fa",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          overflow: "hidden",
          p: 2,
        }}
      >
        {/* Corner Logos */}
        {Object.entries(subCategoryData.logos || {}).map(
          ([position, logo]) =>
            logo?.logoUrl &&
            logo?.enabled && (
              <Box
                key={position}
                component="img"
                src={logo.logoUrl}
                alt={getPositionName(position)}
                sx={{
                  position: "absolute",
                  left: `${logo.left}%`,
                  top: `${logo.top}%`,
                  width: pxToVw(`${logo.width}`),
                  height: pxToVw(`${logo.height}`),
                  objectFit: "contain",
                  opacity: subCategoryData.status ? 1 : 0.5,
                  filter: subCategoryData.status ? "none" : "grayscale(100%)",
                  transition: "all 0.3s ease",

                  zIndex: 5,
                }}
              />
            )
        )}

        {/* Header Preview */}
        {subCategoryData.header.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${subCategoryData.header.left}%`,
              top: `${subCategoryData.header.top}%`,
              color: subCategoryData.header.color,
              fontSize: pxToVw(`${subCategoryData.header.fontSize}`),
              fontWeight: subCategoryData.header.fontWeight,
              textAlign: subCategoryData.header.textAlign,
              opacity: subCategoryData.header.opacity,
              textShadow: textShadowStyle(subCategoryData.header),
              width: "100%",
              zIndex: 10,
            }}
          >
            {subCategoryData.header.text}
          </Box>
        )}

        {/* Description Preview */}
        {subCategoryData.description.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${subCategoryData.description.left}%`,
              top: `${subCategoryData.description.top}%`,
              color: subCategoryData.description.color,
              fontSize: pxToVw(`${subCategoryData.description.fontSize}`),
              fontWeight: subCategoryData.description.fontWeight,
              textAlign: subCategoryData.description.textAlign,
              opacity: subCategoryData.description.opacity,
              textShadow: textShadowStyle(subCategoryData.description),
              width: "100%",
              zIndex: 10,
            }}
          >
            {subCategoryData.description.text}
          </Box>
        )}

        {/* Footer Preview */}
        {subCategoryData.footer.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${subCategoryData.footer.left}%`,
              top: `${subCategoryData.footer.top}%`,
              color: subCategoryData.footer.color,
              fontSize: pxToVw(`${subCategoryData.footer.fontSize}`),
              fontWeight: subCategoryData.footer.fontWeight,
              textAlign: subCategoryData.footer.textAlign,
              opacity: subCategoryData.footer.opacity,
              textShadow: textShadowStyle(subCategoryData.footer),
              zIndex: 10,
              width: "100%",
            }}
          >
            {subCategoryData.footer.text}
          </Box>
        )}

        {/* Search Preview */}
        {subCategoryData.searchConfig.enabled && (
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80%",
              maxWidth: 400,
              zIndex: 10,
            }}
          >
            <input
              type="text"
              placeholder={subCategoryData.searchConfig.placeholder}
              style={{
                width: "100%",
                height: subCategoryData.searchConfig.height,
                padding: subCategoryData.searchConfig.padding,
                backgroundColor: subCategoryData.searchConfig.backgroundColor,
                color: subCategoryData.searchConfig.textColor,
                borderWidth: subCategoryData.searchConfig.borderWidth,
                borderStyle: "solid",
                borderColor: subCategoryData.searchConfig.borderColor,
                borderRadius: subCategoryData.searchConfig.borderRadius,
                fontSize: "14px",
                boxShadow: subCategoryData.searchConfig.boxShadow,
                outline: "none",
              }}
            />
          </Box>
        )}

        {/* Status Indicator */}
        <Chip
          icon={subCategoryData.status ? <Visibility /> : <VisibilityOff />}
          label={subCategoryData.status ? "Active" : "Inactive"}
          color={subCategoryData.status ? "success" : "default"}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
        />
      </Box>
    );
  };

  // Add this function to fetch text data
  const fetchTextData = async (sectionType) => {
    try {
      setLoadingText(true);
      const endpoint =
        `/api/wayfinder/get-main-category-text/${userId}`;

      const response = await axios.get(endpoint);
      if (response.data.success) {
        const data = response.data.data;


        setSubCategoryData(prev => ({
          ...prev,
          header: data.header || prev.header,
          description: data.description || prev.description,
          footer: data.footer || prev.footer,
          searchConfig: data.searchConfig || prev.searchConfig,
        }));

      }
    } catch (error) {
      console.error(`Error fetching ${sectionType} text data:`, error);
      showErrorToast(`Failed to load ${sectionType} text data`);
    } finally {
      setLoadingText(false);
    }
  };

  // Add this function to save text data
  const saveTextData = async (sectionType, sections) => {
    try {
      setLoadingText(true);
      const endpoint = `/api/wayfinder/save-main-subcategory/${userId}`;

      const response = await axios.post(endpoint, sections);
      if (response.data.success) {
        showSuccessToast("Category text settings saved successfully!");
      } else {
        showErrorToast(response.data.message || "Failed to save");
      }
    } catch (error) {
      console.error(`Error saving ${sectionType} text:`, error);
      showErrorToast(`Failed to save ${sectionType} text settings`);
    } finally {
      setLoadingText(false);
    }
  };

  const handleTextSectionChange = (sectionType, section, field, value) => {

    setSubCategoryData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  }

  const handleToggleTextSection = (sectionType, section) => {
    console.log('toggling section', section);

    setSubCategoryData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        enabled: !prev[section].enabled
      }
    }));

  };



  return (
    <>
      <WayfinderMainCategoriesTemplate
        title="Sub Category"
        categoryData={subCategoryData}
        setCategoryData={setSubCategoryData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={loading || uploading}
        setLoading={setLoading}
        message={message}
        setMessage={setMessage}
        fetchInitialData={fetchSubCategoryData}
        handleSave={handleSave}
        handleReset={handleReset}
        renderLivePreview={renderLivePreview}
        handleTextStylingChange={handleTextStylingChange}
        handleUploadLogo={handleUploadLogo}
        handleFieldChange={handleFieldChange}
        handleLogoChange={handleLogoChange}

        getPositionName={getPositionName}
        uploadedUrls={uploadedUrls}
        setUploadedUrls={setUploadedUrls}
        uploadingStates={uploadingStates}
        setUploadingStates={setUploadingStates}

        mediaTitle='Sub Category Media'

        handleDeleteLogo={handleDeleteLogo}
        loadingMedia={loadingMedia}

        handleTextSectionChange={handleTextSectionChange}

        handleToggleTextSection={handleToggleTextSection}
        saveTextData={saveTextData}
        mainTextSections={subCategoryData}
        loadingText={loadingText}

      />
    </>
  );
};

export default WayfinderMainSubCategory;
